import React, { useMemo, useRef, useState } from "react";
import { useDrag } from "react-dnd";

import {
    VariazioneBarDragItem,
    VARIAZIONE_BAR_TYPE,
    VARIAZIONE_RESIZE_LEFT_TYPE,
    VARIAZIONE_RESIZE_RIGHT_TYPE,
} from "./schedulazioneGanttTypes";

import { VariazioneDisponibilita } from "../variazioniDisponibilitaSlice";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useTheme, alpha } from "@mui/material/styles";

// ============================================
// CONSTANTS
// ============================================

export const ALTEZZA_VARIAZIONE = 14; // ~50% della fase frazione (28px)

// ============================================
// SUB-COMPONENT — Resize handle
// ============================================

interface ResizeHandleProps {
    item: VariazioneBarDragItem;
    edge: "left" | "right";
}

const ResizeHandle = ({ item, edge }: ResizeHandleProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const type = edge === "left" ? VARIAZIONE_RESIZE_LEFT_TYPE : VARIAZIONE_RESIZE_RIGHT_TYPE;

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type,
            item: { ...item, type, resizeEdge: edge },
            collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        }),
        [type, item, edge]
    );

    drag(ref);

    return (
        <Box
            ref={ref}
            sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                width: 6,
                [edge]: 0,
                cursor: "ew-resize",
                backgroundColor: isDragging ? "rgba(255,255,255,0.5)" : "transparent",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                zIndex: 1,
            }}
        />
    );
};

// ============================================
// PROPS
// ============================================

interface GanttVariazioneBarProps {
    variazione: VariazioneDisponibilita;
    /** Colore del reparto della variazione (border). */
    coloreReparto?: string;
    /** Posizione assoluta nella riga (top in px). */
    top: number;
    /** Posizione orizzontale come percentuale o valore CSS. */
    left: string;
    width: string;
    onClick?: (variazione: VariazioneDisponibilita) => void;
}

// ============================================
// COMPONENT
// ============================================

const GanttVariazioneBar = ({
    variazione,
    coloreReparto,
    top,
    left,
    width,
    onClick,
}: GanttVariazioneBarProps) => {
    const theme = useTheme();
    const ref = useRef<HTMLDivElement>(null);
    const [hover, setHover] = useState(false);

    const dragItem: VariazioneBarDragItem = useMemo(
        () => ({
            type: VARIAZIONE_BAR_TYPE,
            variazioneId: variazione.id!,
            dataDa: variazione.data_da,
            dataA: variazione.data_a,
        }),
        [variazione.id, variazione.data_da, variazione.data_a]
    );

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: VARIAZIONE_BAR_TYPE,
            item: dragItem,
            collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        }),
        [dragItem]
    );

    drag(ref);

    const positivo = variazione.ore_giornaliere >= 0;
    const sfondo = positivo
        ? alpha(theme.palette.success.light, 0.4)
        : alpha(theme.palette.error.light, 0.4);
    const border = coloreReparto || theme.palette.grey[500];
    const segno = positivo ? "+" : "";
    const etichetta = `${segno}${variazione.ore_giornaliere}h${
        variazione.operatore_display ? " " + variazione.operatore_display : ""
    }`;

    const tooltip = (
        <Box>
            <Typography variant="caption" fontWeight="bold">
                {variazione.operatore_display
                    ? `${segno}${variazione.ore_giornaliere}h per ${variazione.operatore_display}`
                    : `${segno}${variazione.ore_giornaliere}h sul reparto`}
            </Typography>
            <Typography variant="caption" display="block">
                {variazione.reparto_display}
            </Typography>
            <Typography variant="caption" display="block">
                Dal {variazione.data_da} al {variazione.data_a}
            </Typography>
            {variazione.note && (
                <Typography variant="caption" display="block">
                    Note: {variazione.note}
                </Typography>
            )}
        </Box>
    );

    return (
        <Tooltip title={tooltip} placement="top">
            <Box
                ref={ref}
                onClick={(e) => {
                    e.stopPropagation();
                    onClick?.(variazione);
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                sx={{
                    position: "absolute",
                    top,
                    left,
                    width,
                    height: ALTEZZA_VARIAZIONE,
                    backgroundColor: sfondo,
                    border: `3px solid ${border}`,
                    borderRadius: 0.5,
                    cursor: "grab",
                    opacity: isDragging ? 0.4 : 1,
                    display: "flex",
                    alignItems: "center",
                    px: 0.5,
                    overflow: "hidden",
                    transition: "box-shadow 0.15s",
                    boxShadow: hover ? theme.shadows[2] : undefined,
                    pointerEvents: "auto",
                }}
            >
                <ResizeHandle item={dragItem} edge="left" />
                <Typography
                    variant="caption"
                    noWrap
                    sx={{
                        fontSize: "0.6rem",
                        fontWeight: "bold",
                        color: positivo ? theme.palette.success.dark : theme.palette.error.dark,
                        mx: 0.5,
                    }}
                >
                    {etichetta}
                </Typography>
                <ResizeHandle item={dragItem} edge="right" />
            </Box>
        </Tooltip>
    );
};

export default GanttVariazioneBar;
